import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, Search } from "lucide-react";
import {
  FAQ_CATEGORIES,
  FAQS,
  faqAnchorId,
  faqCategoryAnchorId,
  faqSchema,
  type FaqCategory,
} from "@/data/faqs";
import { trackEvent } from "@/lib/analytics";

type Filter = "All" | FaqCategory;

const FaqPage = () => {
  const [filter, setFilter] = useState<Filter>("All");
  const [query, setQuery] = useState("");
  const [openItems, setOpenItems] = useState<string[]>([]);
  const searchDebounce = useRef<number | null>(null);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) => filter === "All" || f.category === filter).filter(
      (f) =>
        q.length === 0 ||
        f.q.toLowerCase().includes(q) ||
        f.a.toLowerCase().includes(q),
    );
  }, [filter, query]);

  const grouped = useMemo(() => {
    const map = new Map<FaqCategory, typeof FAQS>();
    visible.forEach((f) => {
      const arr = map.get(f.category) ?? [];
      arr.push(f);
      map.set(f.category, arr);
    });
    return map;
  }, [visible]);

  const filters: Filter[] = ["All", ...FAQ_CATEGORIES];

  // Deep-link: if URL has a #q-... or #cat-... anchor, scroll & auto-open.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    const target = document.getElementById(hash);
    if (!target) return;
    setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    if (hash.startsWith("q-")) {
      const match = FAQS.find((f) => faqAnchorId(f.q) === hash);
      if (match) setOpenItems((prev) => (prev.includes(hash) ? prev : [...prev, hash]));
    }
  }, []);

  // Debounced search analytics — fires once the user pauses typing.
  useEffect(() => {
    if (searchDebounce.current) window.clearTimeout(searchDebounce.current);
    const q = query.trim();
    if (!q) return;
    searchDebounce.current = window.setTimeout(() => {
      trackEvent("faq_search", {
        query: q.toLowerCase().slice(0, 80),
        result_count: visible.length,
        filter,
      });
    }, 600);
    return () => {
      if (searchDebounce.current) window.clearTimeout(searchDebounce.current);
    };
  }, [query, visible.length, filter]);

  const handleAccordionChange = (next: string[]) => {
    // Detect newly-opened items (Accordion type="multiple" gives us the full array).
    const opened = next.filter((v) => !openItems.includes(v));
    opened.forEach((value) => {
      // value format: `${cat}-${index}`
      const [, idxStr] = value.split(/-(\d+)$/);
      const idx = Number(idxStr);
      const cat = value.replace(/-(\d+)$/, "");
      const item = visible.filter((f) => f.category === cat)[idx];
      if (item) {
        trackEvent("faq_question_open", {
          question: item.q,
          category: item.category,
          source: "faq_page",
        });
      }
    });
    setOpenItems(next);
  };

  const handleFilterClick = (f: Filter) => {
    setFilter(f);
    trackEvent("faq_filter_select", { category: f });
  };

  // Production-safe JSON-LD validator. Verifies that exactly one FAQPage
  // schema is rendered on the page and that every Question entity is
  // unique (no duplicate answers). Console-warns only — never throws.
  useEffect(() => {
    if (typeof document === "undefined") return;
    // Defer to next tick so Helmet has injected its <script> tag.
    const id = window.setTimeout(() => {
      try {
        const scripts = Array.from(
          document.querySelectorAll<HTMLScriptElement>(
            'script[type="application/ld+json"]',
          ),
        );
        const faqPages = scripts
          .map((s) => {
            try {
              return JSON.parse(s.textContent || "null");
            } catch {
              return null;
            }
          })
          .filter(
            (j) => j && (j["@type"] === "FAQPage" || j["@graph"]?.some?.((g: any) => g["@type"] === "FAQPage")),
          );
        if (faqPages.length === 0) {
          console.warn("[faq] No FAQPage JSON-LD found on /faq.");
          return;
        }
        if (faqPages.length > 1) {
          console.warn(
            `[faq] Expected 1 FAQPage JSON-LD on /faq, found ${faqPages.length}. ` +
              "Remove duplicates so Google ingests a single canonical schema.",
          );
        }
        const entities: Array<{ name?: string; acceptedAnswer?: { text?: string } }> =
          faqPages[0]?.mainEntity ?? [];
        const seenQ = new Set<string>();
        const seenA = new Set<string>();
        const dupQ: string[] = [];
        const dupA: string[] = [];
        for (const e of entities) {
          const q = (e?.name ?? "").trim().toLowerCase();
          const a = (e?.acceptedAnswer?.text ?? "").trim().toLowerCase();
          if (q) {
            if (seenQ.has(q)) dupQ.push(q);
            else seenQ.add(q);
          }
          if (a) {
            if (seenA.has(a)) dupA.push(a);
            else seenA.add(a);
          }
        }
        if (dupQ.length || dupA.length) {
          console.warn("[faq] FAQPage JSON-LD has duplicates", {
            duplicate_questions: dupQ,
            duplicate_answers: dupA,
          });
        }
      } catch (err) {
        console.warn("[faq] JSON-LD validation failed", err);
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <div className="pt-20">
      <Helmet>
        <title>Photo Studio Rental FAQ | MyStudio Columbus, GA</title>
        <meta
          name="description"
          content="Answers to common questions about renting a photo and video studio in Columbus, GA — pricing, booking, equipment, parking, and shoot logistics."
        />
        <link rel="canonical" href="https://bookmystudiospace.com/faq" />
        <meta property="og:title" content="Photo Studio Rental FAQ | MyStudio Columbus, GA" />
        <meta
          property="og:description"
          content="Everything you need to know before booking a photo or video studio in Columbus, GA."
        />
        <meta property="og:url" content="https://bookmystudiospace.com/faq" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqSchema(FAQS))}</script>
      </Helmet>

      <section className="py-16 md:py-20 text-center border-b border-border">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="inline-flex items-center gap-2 text-primary font-mono text-xs tracking-widest uppercase mb-4">
            <HelpCircle className="w-4 h-4" /> Frequently Asked Questions
          </div>
          <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-5">
            STUDIO RENTAL FAQ
          </h1>
          <p className="text-muted-foreground font-body text-base md:text-lg max-w-2xl mx-auto">
            Pricing, booking, equipment, parking, and video/photo requirements — answered.
          </p>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Search */}
          <div className="relative mb-6">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions…"
              className="w-full bg-panel border border-border pl-9 pr-3 py-3 font-body text-sm focus:outline-none focus:border-primary"
              aria-label="Search FAQs"
            />
          </div>

          {/* Category filter chips */}
          <div className="flex flex-wrap gap-2 mb-8">
            {filters.map((f) => {
              const active = filter === f;
              return (
                <button
                  key={f}
                  onClick={() => handleFilterClick(f)}
                  className={`px-3 py-1.5 font-mono text-[11px] tracking-widest uppercase border transition-colors ${
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-panel text-foreground/80 border-border hover:border-primary"
                  }`}
                  aria-pressed={active}
                >
                  {f}
                </button>
              );
            })}
          </div>

          {/* Grouped accordions */}
          {visible.length === 0 ? (
            <p className="text-center text-muted-foreground font-body py-12">
              No questions match your search. Try a different keyword or{" "}
              <Link to="/contact" className="text-primary underline">
                contact us
              </Link>
              .
            </p>
          ) : (
            <div className="space-y-10">
              {Array.from(grouped.entries()).map(([cat, items]) => (
                <div key={cat} id={faqCategoryAnchorId(cat)} className="scroll-mt-24">
                  <h2 className="font-display tracking-widest text-lg text-primary mb-4">
                    {cat.toUpperCase()}
                  </h2>
                  <Accordion
                    type="multiple"
                    className="w-full"
                    value={openItems}
                    onValueChange={handleAccordionChange}
                  >
                    {items.map((f, i) => (
                      <AccordionItem
                        key={f.q}
                        value={`${cat}-${i}`}
                        id={faqAnchorId(f.q)}
                        className="border-border scroll-mt-24"
                      >
                        <AccordionTrigger className="text-left font-body text-base hover:no-underline">
                          {f.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-foreground/85 font-body leading-relaxed">
                          {f.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </div>
          )}
          {/* dummy to keep diff context unique */}

          {/* CTA */}
          <div className="mt-16 bg-panel border border-border p-8 text-center">
            <h2 className="font-display tracking-widest text-xl mb-3">
              STILL HAVE QUESTIONS?
            </h2>
            <p className="text-sm text-muted-foreground font-body mb-6 max-w-xl mx-auto">
              Reach out and we'll help you plan your shoot, recommend the right
              studio, and answer anything that isn't covered above.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/book">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-display tracking-widest px-8">
                  BOOK A STUDIO
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-border font-display tracking-widest px-8">
                  CONTACT US
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FaqPage;
